import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Box, Paper, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, TablePagination, TextField, IconButton, Grid, Typography, CircularProgress, Snackbar, Alert, Stack, MenuItem, Select, FormControl, InputLabel, Button } from "@mui/material";
import { Edit, Check as SaveIcon, Close as CancelIcon, RestartAlt, SearchOff as NoDataIcon } from "@mui/icons-material";
import { getData, putData } from "../../../../../services/axiosInstance";
import { API_ENDPOINTS } from "../../../../../services/endpoints";

function LeaveAttendanceStepper({ payPeriod }) {
    const [batchOverview, setBatchOverview] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");

    // --- Filter & Sort States ---
    const [sortConfig, setSortConfig] = useState({ key: "", direction: "" }); // direction: 'asc' or 'desc'

    // --- Edit States ---
    const [editingRowId, setEditingRowId] = useState(null);
    const [editValue, setEditValue] = useState("");
    const [saveLoading, setSaveLoading] = useState(false);

    // --- Feedback States ---
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    // Pagination States
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const fetchBatchData = useCallback(async () => {
        if (!payPeriod) return;

        const getShortMonth = (fullMonth) => {
            return new Date(`${fullMonth} 1, 2020`).toLocaleString("en-US", {
                month: "short",
            });
        };

        setLoading(true);
        const [monthName, year] = payPeriod.split(" ");
        const shortMonth = getShortMonth(monthName);

        try {
            const res = await getData(API_ENDPOINTS.getBatchOverview(year, shortMonth));
            if (res?.statusCode === 200) {
                setBatchOverview(res.result || []);
            }
        } catch (error) {
            setBatchOverview([]);
             setSnackbar({ open: true, message: "Error fetching batch data", severity: "error" });
        } finally {
            setLoading(false);
        }
    }, [payPeriod]);

    useEffect(() => {
        fetchBatchData();
    }, [fetchBatchData]);

    // --- Handlers ---
    const handleStartEdit = (row) => {
        setEditingRowId(row.id);
        setEditValue(row.lopCount === 0 ? "0" : row.lopCount.toString());
    };

    const handleCancelEdit = () => {
        setEditingRowId(null);
        setEditValue("");
    };

    const handleSaveEdit = async (row) => {
        const numericLop = Number(editValue);
        if (numericLop > row.numOfDays) {
            setSnackbar({ open: true, message: `LOP cannot exceed total days (${row.numOfDays})`, severity: "error" });
            return;
        }

        setSaveLoading(true);
        try {
            const res = await putData(API_ENDPOINTS.updateBatchPayrollById, { id: row.id, lopCount: numericLop });
            if (res?.statusCode === 200) {
                setSnackbar({ open: true, message: "Payroll updated successfully", severity: "success" });
                setEditingRowId(null);
                fetchBatchData();
            }
        } catch (error) {
            setSnackbar({ open: true, message: "Error updating LOP", severity: "error" });
        } finally {
            setSaveLoading(false);
        }
    };

    const handleReset = () => {
        setSearchQuery("");
        setSortConfig({ key: "", direction: "" });
        setPage(0);
    };

    // --- Search & Sort Logic ---
    const filteredEmployees = useMemo(() => {
        let data = [...batchOverview];

        // 1. Search
        if (searchQuery) {
            data = data.filter((emp) =>
                emp.empId?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                emp.name?.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }

        // 2. Sort
        if (sortConfig.key) {
            data.sort((a, b) => {
                const valA = parseFloat(a[sortConfig.key] || 0);
                const valB = parseFloat(b[sortConfig.key] || 0);
                return sortConfig.direction === 'asc' ? valA - valB : valB - valA;
            });
        }

        return data;
    }, [batchOverview, searchQuery, sortConfig]);

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    if (loading && !saveLoading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>;
    }

    return (
        <Box>
            <Grid container spacing={2} mb={3}>
                <Grid size={{ xs: 12, sm: 7 }}>
                    <TextField
                        size="small"
                        fullWidth
                        placeholder="Search employee by ID or name"
                        value={searchQuery}
                        onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 2 }}>
                    <FormControl size="small" fullWidth>
                        <InputLabel>Sort by LOP</InputLabel>
                        <Select
                            label="Sort by LOP Amount"
                            value={sortConfig.key === 'lopAmount' ? sortConfig.direction : ""}
                            onChange={(e) => setSortConfig({ key: 'lopAmount', direction: e.target.value })}
                        >
                            <MenuItem value="asc">Low to High</MenuItem>
                            <MenuItem value="desc">High to Low</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 2 }}>
                    <FormControl size="small" fullWidth>
                        <InputLabel>Sort by Gross</InputLabel>
                        <Select
                            label="Sort by Gross"
                            value={sortConfig.key === 'totalGross' ? sortConfig.direction : ""}
                            onChange={(e) => setSortConfig({ key: 'totalGross', direction: e.target.value })}
                        >
                            <MenuItem value="asc">Low to High</MenuItem>
                            <MenuItem value="desc">High to Low</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 1 }}>
                    <Button fullWidth startIcon={<RestartAlt />} onClick={handleReset} sx={{ textTransform: 'none', color: 'text.secondary' }}>Reset</Button>
                </Grid>
            </Grid>

            <TableContainer component={Paper} sx={{ borderRadius: '12px', border: '1px solid #E2E8F0', boxShadow: 'none' }}>
                <Table sx={{ minWidth: 900 }}>
                    <TableHead sx={{ bgcolor: '#EFF6FF' }}>
                        <TableRow >
                            <TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }}>Employee ID</TableCell>
                            <TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }}>Name</TableCell>
                            <TableCell align="center" sx={{ color: '#1D4ED8', fontWeight: 700 }}>No. of Days</TableCell>
                            <TableCell align="center" sx={{ color: '#1D4ED8', fontWeight: 700 }}>Holidays</TableCell>
                            <TableCell align="center" sx={{ color: '#1D4ED8', fontWeight: 700 }}>Leaves</TableCell>
                            <TableCell align="center" sx={{ color: '#1D4ED8', fontWeight: 700 }}>LOP Count</TableCell>
                            <TableCell align="center" sx={{ color: '#1D4ED8', fontWeight: 700 }}>Working Days</TableCell>
                            <TableCell align="right" sx={{ color: '#1D4ED8', fontWeight: 700 }}>LOP Amount</TableCell>
                            <TableCell align="right" sx={{ color: '#1D4ED8', fontWeight: 700 }}>Working Gross</TableCell>
                            <TableCell align="center" sx={{ color: '#1D4ED8', fontWeight: 700 }}>Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody sx={{ bgcolor: '#FFFFFF' }}>
                        {filteredEmployees.length > 0 ? (
                            filteredEmployees.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                                <TableRow key={row.id} hover sx={{
                                    '&:hover': {
                                        bgcolor: '#F1F5F9 !important'
                                    }
                                }}>
                                    <TableCell sx={{ fontWeight: 600 }}>{row.empId}</TableCell>
                                    <TableCell>{row.name}</TableCell>
                                    <TableCell align="center">{row.numOfDays}</TableCell>
                                    <TableCell align="center">{row.holidays}</TableCell>
                                    <TableCell align="center">{row.leaves}</TableCell>
                                    <TableCell align="center">
                                        {editingRowId === row.id ? (
                                            <TextField
                                                variant="standard" type="number" size="small" autoFocus
                                                value={editValue} onFocus={(e) => e.target.select()}
                                                onChange={(e) => setEditValue(e.target.value)}
                                                sx={{ width: 50 }} inputProps={{ style: { textAlign: 'center', fontWeight: 700 } }}
                                            />
                                        ) : (
                                            <Typography sx={{ color: row.lopCount > 0 ? 'error.main' : 'inherit', fontWeight: row.lopCount > 0 ? 700 : 400 }}>
                                                {row.lopCount}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell align="center">{row.workingDays}</TableCell>
                                    <TableCell align="right">₹{parseFloat(row.lopAmount || 0).toLocaleString()}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>₹{parseFloat(row.totalGross || 0).toLocaleString()}</TableCell>
                                    <TableCell align="center">
                                        {editingRowId === row.id ? (
                                            <Stack direction="row" spacing={1} justifyContent="center">
                                                <IconButton size="small" color="success" onClick={() => handleSaveEdit(row)} disabled={saveLoading}>
                                                    {saveLoading ? <CircularProgress size={20} /> : <SaveIcon fontSize="small" />}
                                                </IconButton>
                                                <IconButton size="small" color="error" onClick={handleCancelEdit} disabled={saveLoading}>
                                                    <CancelIcon fontSize="small" />
                                                </IconButton>
                                            </Stack>
                                        ) : (
                                            <IconButton size="small" color="primary" onClick={() => handleStartEdit(row)}>
                                                <Edit fontSize="small" />
                                            </IconButton>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={10} align="center" sx={{ py: 10 }}>
                                    <NoDataIcon sx={{ fontSize: 60, color: '#CBD5E1', mb: 1 }} />
                                    <Typography variant="h6" color="textSecondary">No Employees Found</Typography>
                                    <Typography variant="body2" color="textSecondary">Try adjusting your search or filters</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                component="div" count={filteredEmployees.length}
                rowsPerPage={rowsPerPage} page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25]}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} variant="filled" sx={{ width: '100%', borderRadius: 2 }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default LeaveAttendanceStepper;